/** Responsibility: the live event feed, pushed over the hub WebSocket. */
import { useMemo, useState } from 'react';
import { PageContainer, SectionCard } from '@kit/components';
import { Alert, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { EventCard } from '../components/events/EventCard';
import { PopupPermissionWizard } from '../components/setup/PopupPermissionWizard';
import { useEvents } from '../api/hooks/useEvents';
import type { StockEvent } from '../api/client';
import type { LiveEvents } from '../hooks/useLiveEvents';

type LivePageProps = {
  live: LiveEvents;
  autoOpenEnabled: boolean;
  onAutoOpenChange: (enabled: boolean) => void;
  lastOutcome: string | null;
};

export function LivePage({ live, autoOpenEnabled, onAutoOpenChange, lastOutcome }: LivePageProps) {
  const { data: history } = useEvents(50);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Socket events first, then anything from before the page loaded.
  const merged = useMemo<StockEvent[]>(() => {
    const seen = new Set(live.events.map((e) => e.id));
    const older = (history ?? []).filter((e) => !seen.has(e.id));
    return [...live.events, ...older];
  }, [live.events, history]);

  return (
    <PageContainer>
      {/* See WatchlistPage: a flex item defaults to min-width:auto, so anything
          with a large intrinsic width would widen this column and slide the
          whole page sideways on a phone. */}
      <Stack spacing={3} sx={{ minWidth: 0 }}>

      {/* The switch moved down out of the old header band, which was
          otherwise a line of prose taking up the top of the page. */}
      <Stack direction="row" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
        <FormControlLabel
          control={
            <Switch
              checked={autoOpenEnabled}
              onChange={(e) => {
                if (e.target.checked) {
                  setWizardOpen(true);
                  return;
                }
                onAutoOpenChange(false);
              }}
            />
          }
          label="Auto-open tabs"
        />
      </Stack>

      {!live.connected ? (
        <Alert severity="warning">
          Live feed disconnected. Reconnecting with backoff; events during this window will
          appear only after a reload.
        </Alert>
      ) : null}

      {lastOutcome === 'blocked' ? (
        <Alert
          severity="warning"
          action={
            <Button size="small" onClick={() => setWizardOpen(true)}>
              Fix
            </Button>
          }
        >
          The browser blocked an automatic tab. A notification was sent instead. Run the setup
          check to allowlist this origin.
        </Alert>
      ) : null}

      {lastOutcome === 'rate_limited' ? (
        <Alert severity="info">
          Tab limit reached for this minute. Further detections notify instead of opening, so a
          wave of SKUs cannot bury the one that mattered.
        </Alert>
      ) : null}

      <SectionCard title="Feed" subtitle={`${merged.length} events`}>
        {merged.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing yet. Events appear here the moment a node reports one.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {merged.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </Stack>
        )}
      </SectionCard>

      <PopupPermissionWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onVerified={() => onAutoOpenChange(true)}
      />
      </Stack>
    </PageContainer>
  );
}
